// ─────────────────────────────────────────────────────────────────────────────
// SuggestedPrompts – Pre-populated prompt chips based on detected ESG context
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import type { ESGContext } from "../types";

const ESG_DOMAIN_LABELS: Record<string, { label: string; emoji: string; color: string }> = {
	environmental: { label: "Environmental", emoji: "🌿", color: "#14532d" },
	climate: { label: "Climate Disclosure", emoji: "🌍", color: "#0c4a6e" },
	social: { label: "Social Metrics", emoji: "🤝", color: "#713f12" },
	governance_esg: { label: "ESG Governance", emoji: "🏛", color: "#4c1d95" },
	esg_combined: { label: "ESG Report", emoji: "♻️", color: "#166534" },
	unknown: { label: "ESG Data", emoji: "📊", color: "#166534" },
};

interface SuggestedPromptsProps {
	prompts: string[];
	esgContext?: ESGContext | null;
	entity?: string;
	period?: string;
	onSelectPrompt: (prompt: string) => void;
	isVisible: boolean;
}

export default function SuggestedPrompts({
	prompts,
	esgContext,
	entity,
	period,
	onSelectPrompt,
	isVisible,
}: SuggestedPromptsProps) {
	if (!isVisible || prompts.length === 0) return null;

	const esgInfo = esgContext
		? ESG_DOMAIN_LABELS[esgContext.domain] ?? ESG_DOMAIN_LABELS.unknown
		: null;

	const sharedEntity = entity || esgContext?.entity;
	const sharedPeriod = period || esgContext?.period;

	return (
		<div
			style={{
				padding: "12px 14px",
				borderBottom: "1px solid #e2e8f0",
				background: "#fafbff",
			}}
		>
			{/* Context detection badges */}
			<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
				{esgInfo && (
					<ContextBadge
						emoji={esgInfo.emoji}
						label={esgInfo.label}
						bgFrom="#dcfce7"
						bgTo="#d1fae5"
						borderColor="#86efac"
						textColor={esgInfo.color}
						sub={
							esgContext?.frameworks.slice(0, 2).join(" / ") ||
							(sharedEntity ? sharedEntity : undefined)
						}
						period={sharedPeriod}
					/>
				)}
			</div>

			{/* Prompts label */}
			<div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6, fontWeight: 500 }}>
				Suggested questions:
			</div>

			{/* Prompt chips */}
			<div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
				{prompts.map((prompt, i) => (
					<button
						key={i}
						onClick={() => onSelectPrompt(prompt)}
						title={prompt}
						style={{
							display: "block",
							width: "100%",
							textAlign: "left",
							padding: "6px 10px",
							background: "#fff",
							border: "1px solid #e2e8f0",
							borderRadius: 8,
							fontSize: 12,
							color: "#334155",
							cursor: "pointer",
							transition: "all 0.15s",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							lineHeight: 1.4,
						}}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLButtonElement).style.background = "#F5F3F8";
							(e.currentTarget as HTMLButtonElement).style.borderColor = "#482A7A";
							(e.currentTarget as HTMLButtonElement).style.color = "#482A7A";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLButtonElement).style.background = "#fff";
							(e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
							(e.currentTarget as HTMLButtonElement).style.color = "#334155";
						}}
					>
						<span style={{ marginRight: 6, opacity: 0.6 }}>→</span>
						{prompt}
					</button>
				))}
			</div>
		</div>
	);
}

// ── Context Badge ─────────────────────────────────────────────────────────────

function ContextBadge({
	emoji,
	label,
	bgFrom,
	bgTo,
	borderColor,
	textColor,
	sub,
	period,
}: {
	emoji: string;
	label: string;
	bgFrom: string;
	bgTo: string;
	borderColor: string;
	textColor: string;
	sub?: string;
	period?: string;
}) {
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 5,
				padding: "4px 10px",
				background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})`,
				borderRadius: 20,
				fontSize: 11,
				fontWeight: 600,
				color: textColor,
				border: `1px solid ${borderColor}`,
				flexWrap: "wrap",
			}}
		>
			<span>{emoji}</span>
			<span>{label}</span>
			{sub && <span style={{ opacity: 0.75 }}>• {sub.slice(0, 22)}</span>}
			{period && <span style={{ opacity: 0.75 }}>• {period}</span>}
		</div>
	);
}
